import React, { useCallback, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import LocationField from "venuescanner-js/react/components/SelectField/LocationField";
import EventTypeField from "venuescanner-js/react/components/SelectField/EventTypeField";

import { locations } from "venuescanner-js/vanilla/components/locations";
import cl from "classnames";
import classes from "./styles.module.scss";
import queryToUrl from "venuescanner-js/react/components/Srp/helpers/queryToUrl";
import GuestsField from "./GuestsField";
import SearchButton from "./SearchButton";
import ErrorPage from "venuescanner-js/react/components/ErrorPage";
import isSSR from "venuescanner-js/services/isSSR";
import fixDropDownPositionMobile from "venuescanner-js/react/components/SelectField/fixMobileMenuPosition";
import { reportError } from "venuescanner-js/react/services/errorService";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "venuescanner-js/theme";

import SearchBarMui from "./SearchBarMui";

// Mobile devices scroll when the select fields open, helps to avoid the keyboard.
const handleMobileScrollIssue = (ref) => {
  if (isSSR()) return true;
  if (!window.matchMedia("screen and (max-width: 768px)").matches) return true;
  setTimeout(() => {
    const rect = ref.current.getBoundingClientRect?.();
    if (rect) {
      const y = rect.top - 60;
      if (y > (window.scrollY || window.pageYOffset)) {
        window.scrollTo({
          top: y,
          behavior: "smooth",
        });
        setTimeout(fixDropDownPositionMobile, 700);
      }
    }
    return true;
  }, 0);
};

const SearchBar = ({
  place,
  tag,
  venueTag,
  isForm = false,
  isHomepage = false,
}) => {
  // let Optimize set the options
  if (!isSSR()) {
    if (window.isForm !== undefined) isForm = window.isForm;
    if (window.isHomepage !== undefined) isHomepage = window.isHomepage;
  }
  const [location, setLocation] = useState(place);
  const [eventType, setEventType] = useState(tag);
  const [venueType] = useState(venueTag);
  const [guests, setGuests] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [rendered, setRendered] = useState(false);
  const [useForm, setUseForm] = useState(isForm);
  const [useHomepage, setUseHomepage] = useState(isHomepage);
  const locationRef = useRef(null);
  const eventRef = useRef(null);

  // let Optimize set the options is searchbar rendered first
  if (!isSSR()) {
    window.setIsForm = (val) => {
      setUseForm(val);
    };
    window.setIsHomepage = (val) => {
      setUseHomepage(val);
    };
  }

  const handleSubmit = useCallback(() => {
    if (isSSR()) return;
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "home_form_submission",
        guests,
        event_type: eventType && eventType.value,
        location: location && location.label,
      });
    }

    setLoading(true);
    let newUrl;
    if (location.value.match(/\/book\//)) {
      newUrl = location.value
    } else {
      newUrl = queryToUrl({
        city_slug: location.value,
        event_type: eventType,
        type: venueType ? [`${venueType.label}`] : null,
        capacity: guests,
      });
    }
    document.location = newUrl;
  }, [location, eventType, venueType, guests]);

  useEffect(() => {
    if (
      !isSSR() &&
      !location &&
      window.data &&
      window.data.user &&
      window.data.user.place &&
      window.data.user.place.id
    ) {
      // console.log(locations.find)
      locations.find(window.data.user.place.id).then((place) => {
        // console.log(place)
        if (place && place.slug) {
          setLocation({
            value: place.slug,
            label: place.name,
          });
        }
      });
    }
  }, []);

  const hasLocation = !!location?.value;
  useEffect(() => {
    // console.log('Searchbar')
    if (!isSSR())
      setTimeout(() => {
        setRendered(true);
      });
  });

  return isHomepage ? (
    <div
      className={cl(classes.SearchBarContainerForm, {
        [classes["SearchBarContainer--rendered"]]: rendered,
      })}
      style={{
        maxHeight: "247px",
        opacity: 0,
        transition: "opacity 0.3s, max-height 0.3s",
      }}
    >
      <div className={classes.LocationFieldContainer} ref={locationRef}>
        <LocationField
          id="SearchBar_location"
          value={location}
          onChange={(loc) => {
            setLocation(loc);
          }}
          absolute={true}
          onFocus={() => handleMobileScrollIssue(locationRef)}
          data-ga="new_SearchBar_location"
          isForm={useForm}
        />
      </div>
      <div className={classes.EventTypeFieldContainer} ref={eventRef}>
        <EventTypeField
          id="SearchBar_event"
          value={eventType ? eventType.value || eventType : null}
          onChange={setEventType}
          onFocus={() => handleMobileScrollIssue(eventRef)}
          absolute={true}
          clearButton
          data-ga="new_SearchBar_event_type"
          isForm={useForm}
        />
      </div>
      <GuestsField
        value={guests}
        onChange={setGuests}
        data-ga="new_SearchBar_guest_slider"
        isForm={useForm}
      />
      <SearchButton
        data-ga="new_SearchBar_button"
        onClick={() => {
          if (hasLocation) {
            handleSubmit();
          } else {
            locationRef.current.focus();
          }
        }}
        isLoading={isLoading}
        isForm={useForm}
        isHomepage={useHomepage}
        hasLocation={hasLocation}
      />
    </div>
  ) : (
    <SearchBarMui tag={tag} place={place} />
  );
};

SearchBar.propTypes = {
  place: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    slug: PropTypes.string,
    name: PropTypes.string,
  }),
  tag: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    is_corporate: PropTypes.bool,
    searchable_by: PropTypes.string,
  }),
  venueTag: PropTypes.string,
  isForm: PropTypes.bool,
  isHomepage: PropTypes.bool,
};

class ErrorBoundary extends React.Component {
  state = {
    error: null,
    errorInfo: null,
  };

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    reportError("SearchBar", error, errorInfo);
  }

  render() {
    const { state, props } = this;
    // console.log({ props })
    if (state.error) {
      return <ErrorPage />;
    }
    return (
      <ThemeProvider theme={theme}>
        <SearchBar {...props} />
      </ThemeProvider>
    );
  }
}
export default ErrorBoundary;
